import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { showSuccessToast, showAlertToast } from '@medflex/components/stories/Toast';
import Spinner from '@medflex/components/stories/Spinner';
import useIsWideScreen from '@medflex/hooks/useIsWideScreen';
import { trackEvent, TRACKING_ACTIONS, TRACKING_ELEMENT_TYPES } from '@medflex/utils/Tracking';
import isPatientUserId from '@medflex/utils/isPatientUserId';
import useIsModal from '../../hooks/useIsModal';
import useCountries from '../../hooks/useCountries';
import useAddressBookUsers from '../../hooks/useAddressBookUsers';
import isSubContactId from '../../../utils/isSubContactId';
import ContactInfoBody from './ContactInfoBody';

const ContactInfoModule = ({
  userId,
  edit,
  handleEdit,
  handleCancelEdit,
  handleAfterSubmit,
  hideDoB,
  hideHeader,
}) => {
  const [isLoading, setIsLoading] = useState();
  const {
    users,
    load: loadAddressBookUsers,
    isLoading: isAddressBookUserLoading,
  } = useAddressBookUsers();
  const { t } = useTranslation('physician', { keyPrefix: 'ContactInfoModule' });
  const dispatch = useDispatch();
  const { isModal } = useIsModal();
  const isWideScreen = useIsWideScreen();
  const countries = useCountries();

  useEffect(() => {
    if (userId) {
      loadAddressBookUsers([userId]);
    }
  }, [loadAddressBookUsers, userId]);

  const user = users[userId];

  if (isEmpty(user)) {
    return null;
  }

  const isNotesEditable = isPatientUserId(user.userId) && !!user.pairedAt;

  const isContactInfoEditable = isSubContactId(user.userId);

  const handleSubmit = async values => {
    try {
      setIsLoading(true);
      await dispatch.chatContacts.updatePatientData({
        contactId: user.contactId,
        payload: {
          ...(values.firstName && { firstName: values.firstName.trim() }),
          ...(values.lastName && { lastName: values.lastName.trim() }),
          ...(values.dateOfBirth && { dateOfBirth: values.dateOfBirth }),
          ...(typeof values.note !== 'undefined'
            ? values.note && { note: values.note.trim() }
            : { note: user.note }),
          ...(typeof values.pvsId !== 'undefined'
            ? values.pvsId && { pvsId: values.pvsId.trim() }
            : { pvsId: user.pvsId }),
        },
      });

      showSuccessToast(t('toasts.success.title', ''), null, {
        toastId: 'patientInfoChangeSuccess',
      });

      if (values.note) {
        trackEvent({
          zone: 'Contacts | form',
          action: TRACKING_ACTIONS.SUBMIT,
          elementType: TRACKING_ELEMENT_TYPES.FORM,
          actionText: `textarea filled (${values.note.trim().length}) | physician/staff`,
        });
      }

      setIsLoading(false);
      handleAfterSubmit();
    } catch {
      showAlertToast(t('toasts.failure.title'), t('toasts.failure.text'), {
        toastId: 'patientInfoChangeFailed',
      });
      setIsLoading(false);
    }
  };

  return (
    <div data-component="ContactInfo" className="flex flex-col max-h-full space-y-4">
      {isLoading || isAddressBookUserLoading ? (
        <Spinner className="self-center" />
      ) : (
        <ContactInfoBody
          isContactInfoEditable={isContactInfoEditable}
          user={user}
          country={user.countryCode && countries[user.countryCode]}
          isNotesEditable={isNotesEditable}
          edit={edit}
          state={isLoading ? 'loading' : 'composing'}
          handleSubmit={handleSubmit}
          autoFocus={isModal && isWideScreen}
          handleEdit={handleEdit}
          handleCancelEdit={handleCancelEdit}
          mainContact={user.mainContact}
          subContacts={user.subContacts}
          hideDoB={hideDoB}
          hideHeader={hideHeader}
        />
      )}
    </div>
  );
};

ContactInfoModule.defaultProps = {
  edit: false,
  handleEdit: () => {},
  handleAfterSubmit: () => {},
  handleCancelEdit: () => {},
  hideDoB: false,
  hideHeader: false,
};

ContactInfoModule.propTypes = {
  userId: PropTypes.string.isRequired,
  edit: PropTypes.bool,
  handleEdit: PropTypes.func,
  handleCancelEdit: PropTypes.func,
  handleAfterSubmit: PropTypes.func,
  hideDoB: PropTypes.bool,
  hideHeader: PropTypes.bool,
};

export default ContactInfoModule;
